import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import '../styles/TimeTracking.css';
import {
    FiClock, FiSearch, FiFilter, FiCalendar, FiAlertCircle, FiBriefcase, FiCheckCircle, FiSave, FiX, FiEdit2,
} from 'react-icons/fi';
import Cookies from 'js-cookie';
import useDebounce from '../../../hooks/useDebounce';
import CommonLoader from '../../../components/common/CommonLoader';
import { GRAPHQL_HTTP_URL } from '../../../config/api';

// ─── Constants ────────────────────────────────────────────────────────────────

const GQL = GRAPHQL_HTTP_URL;

const DATE_RANGE_OPTIONS = ['Last 30 days', 'Last 7 days', 'This Month', 'Last Month', 'This Week', 'Last Week'];
const LEAVE_OPTIONS = [
    { value: 'All', label: 'Leave' },
    { value: 'Paid', label: 'Paid Leave' },
    { value: 'Unpaid', label: 'Unpaid Leave' },
    { value: 'None', label: 'None' },
];
const LEAVE_EDIT_OPTIONS = ['None', 'Paid Leave', 'Unpaid Leave', 'Sick Leave', 'Annual Leave', 'Compensation Leave'];
const DAILY_REGULAR_HOURS_THRESHOLD = 8;
const CACHE_TTL_MS = 2 * 60 * 1000;
const REQUEST_TIMEOUT_MS = 30000;

// ─── Utility helpers ──────────────────────────────────────────────────────────

const toDateStr = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const getWeekStart = (offsetWeeks = 0) => {
    const d = new Date();
    const dayOfWeek = d.getDay();
    const diffToMonday = d.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
    d.setDate(diffToMonday + offsetWeeks * 7);
    d.setHours(0, 0, 0, 0);
    return d;
};

const getPeriodDates = (period) => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    switch (period) {
        case 'Last Week': {
            const ws = getWeekStart(-1);
            const we = new Date(ws); we.setDate(ws.getDate() + 6);
            return { startDate: toDateStr(ws), endDate: toDateStr(we) };
        }
        case 'This Month': {
            const start = new Date(now.getFullYear(), now.getMonth(), 1);
            const end   = new Date(now.getFullYear(), now.getMonth() + 1, 0);
            return { startDate: toDateStr(start), endDate: toDateStr(end) };
        }
        case 'Last Month': {
            const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            const end   = new Date(now.getFullYear(), now.getMonth(), 0);
            return { startDate: toDateStr(start), endDate: toDateStr(end) };
        }
        case 'Last 7 days': {
            const start = new Date(now); start.setDate(now.getDate() - 6);
            return { startDate: toDateStr(start), endDate: toDateStr(now) };
        }
        case 'Last 30 days': {
            const start = new Date(now); start.setDate(now.getDate() - 29);
            return { startDate: toDateStr(start), endDate: toDateStr(now) };
        }
        case 'This Week':
        default: {
            const ws = getWeekStart(0);
            const we = new Date(ws); we.setDate(ws.getDate() + 6);
            return { startDate: toDateStr(ws), endDate: toDateStr(we) };
        }
    }
};

const toHHMM = (minutes) => {
    const normalized = ((minutes % (24 * 60)) + (24 * 60)) % (24 * 60);
    const h = String(Math.floor(normalized / 60)).padStart(2, '0');
    const m = String(normalized % 60).padStart(2, '0');
    return `${h}:${m}`;
};

const fmtDisplayDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ─── Data mapping — one row per timesheet entry ───────────────────────────────

const mapApiResponse = (summary) => {
    if (!summary) return {
        rows: [],
        totals: { totalRegularHours: 0, totalOvertimeHours: 0, totalPaidLeaveHours: 0, totalUnpaidLeaveHours: 0 },
    };

    const empMap = Object.fromEntries(
        (summary.employees ?? []).map((e) => [e.employeeId, e]),
    );
    const rows = (summary.entries ?? []).map((entry, index) => {
        const emp     = empMap[entry.employeeId] ?? {};
        const isLeave = entry.leaveType && entry.leaveType !== '—' && entry.leaveType.toLowerCase() !== 'none';

        let hoursWorked = entry.regularHours  ?? 0;
        let otHours     = entry.overtimeHours ?? 0;
        if (!isLeave) {
            const totalShift = hoursWorked + otHours;
            if (totalShift > 8) {
                hoursWorked = 8;
                otHours     = Math.round((totalShift - 8) * 100) / 100;
            }
        }

        return {
            key:               `${entry.employeeId}-${entry.workDate}-${index}`,
            employeeId:        entry.employeeId,
            employeeName:      emp.fullName ?? 'Unknown',
            role:              emp.employmentType ?? '—',
            workDate:          entry.workDate,
            hoursWorked,
            otHours,
            leaveType:         entry.leaveType ?? '—',
            totalPayableHours: isLeave
                ? (entry.totalPayableHours ?? 0)
                : Math.round((hoursWorked + otHours) * 100) / 100,
        };
    });

    return {
        rows,
        totals: {
            totalRegularHours:    summary.totalRegularHours    ?? 0,
            totalOvertimeHours:   summary.totalOvertimeHours   ?? 0,
            totalPaidLeaveHours:  summary.totalPaidLeaveHours  ?? 0,
            totalUnpaidLeaveHours: summary.totalUnpaidLeaveHours ?? 0,
        },
    };
};

// ─── GraphQL query ────────────────────────────────────────────────────────────

const TIME_TRACKING_QUERY = `
  query GetPayrollTimeTracking($filter: TimeTrackingFilterInput!) {
    payrollTimeTracking(filter: $filter) {
      totalRegularHours
      totalOvertimeHours
      totalPaidLeaveHours
      totalUnpaidLeaveHours
      totalShiftCount
      employees { employeeId fullName employmentType }
      entries {
        employeeId
        workDate
        regularHours
        overtimeHours
        leaveType
        totalPayableHours
      }
    }
  }
`;

// ─── Component ────────────────────────────────────────────────────────────────

export default function TimeTracking({ initialSearch = '' }) {
    const [search, setSearch]             = useState(initialSearch);
    const [leaveFilter, setLeaveFilter]   = useState('All');
    const [dateRange, setDateRange]       = useState('Last 30 days');
    const [employees, setEmployees]       = useState([]);
    const [totals, setTotals]             = useState({ totalRegularHours: 0, totalOvertimeHours: 0, totalPaidLeaveHours: 0, totalUnpaidLeaveHours: 0 });
    const [loading, setLoading]           = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError]               = useState(null);
    const [editingRows, setEditingRows]   = useState({});
    const [savingRowKey, setSavingRowKey] = useState(null);
    const [editConfirmRow, setEditConfirmRow] = useState(null);

    const debouncedSearch        = useDebounce(search, 300);
    const token                  = Cookies.get('accessToken');
    const cacheRef               = useRef(new Map());
    const hasLoadedDataRef       = useRef(false);
    const inFlightRequestKeyRef  = useRef(null);

    const normalizeLeaveType = (value) => (value || '').toLowerCase();

    const matchesLeaveFilter = useCallback((row) => {
        const leave   = normalizeLeaveType(row.leaveType);
        const isEmpty = !leave || leave === '—' || leave === 'none' || leave === 'null' || leave === '';
        if (leaveFilter === 'All')    return true;
        if (leaveFilter === 'None')   return isEmpty;
        if (leaveFilter === 'Unpaid') return leave.includes('unpaid');
        if (leaveFilter === 'Paid')   return !isEmpty && !leave.includes('unpaid');
        return true;
    }, [leaveFilter]);

    const executeGraphQL = useCallback(async (query, variables, signal) => {
        const controller = new AbortController();
        let timedOut = false;
        const timeoutId = setTimeout(() => { timedOut = true; controller.abort(); }, REQUEST_TIMEOUT_MS);
        const onAbort = () => controller.abort();
        if (signal) signal.addEventListener('abort', onAbort, { once: true });

        let res;
        try {
            res = await fetch(GQL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ query, variables }),
                signal: controller.signal,
            });
        } catch (err) {
            if (timedOut) throw new Error(`Payroll API request timed out after ${REQUEST_TIMEOUT_MS / 1000}s`);
            throw err;
        } finally {
            clearTimeout(timeoutId);
            if (signal) signal.removeEventListener('abort', onAbort);
        }

        let json;
        try   { json = await res.json(); }
        catch { throw new Error(`Invalid response from ${GQL} (HTTP ${res.status})`); }

        if (!res.ok)              throw new Error(json?.errors?.[0]?.message || `HTTP ${res.status}`);
        if (json?.errors?.length) throw new Error(json.errors[0]?.message    || 'GraphQL request failed');
        return json.data;
    }, [token]);

    // ── Fetch ─────────────────────────────────────────────────────────────────
    const fetchTimeTracking = useCallback(async (signal, options = {}) => {
        const { force = false } = options;
        const requestKey = `${dateRange}-${force ? 'force' : 'normal'}`;
        if (inFlightRequestKeyRef.current === requestKey) return;

        const cached       = cacheRef.current.get(dateRange);
        const isCacheFresh = cached && (Date.now() - cached.ts < CACHE_TTL_MS);

        if (isCacheFresh && !force) {
            setEmployees(cached.rows);
            setTotals(cached.totals);
            hasLoadedDataRef.current = true;
            setLoading(false);
            return;
        }

        if (hasLoadedDataRef.current) setIsRefreshing(true);
        else setLoading(true);
        setError(null);
        inFlightRequestKeyRef.current = requestKey;

        try {
            const filter = getPeriodDates(dateRange);
            const data   = await executeGraphQL(TIME_TRACKING_QUERY, { filter }, signal);
            const mapped = mapApiResponse(data?.payrollTimeTracking);
            setEmployees(mapped.rows);
            setTotals(mapped.totals);
            hasLoadedDataRef.current = true;
            if (mapped.rows.length > 0) {
                cacheRef.current.set(dateRange, { rows: mapped.rows, totals: mapped.totals, ts: Date.now() });
            } else {
                cacheRef.current.delete(dateRange);
            }
        } catch (err) {
            if (err.name === 'AbortError') return;
            console.error('[TimeTracking] fetch failed:', err);
            setError(err.message ?? 'Failed to load timesheet data.');
            if (!hasLoadedDataRef.current) setEmployees([]);
        } finally {
            if (inFlightRequestKeyRef.current === requestKey) inFlightRequestKeyRef.current = null;
            setLoading(false);
            setIsRefreshing(false);
        }
    }, [dateRange, executeGraphQL]);

    const handleEditChange = (rowKey, field, value) => {
        setEditingRows((prev) => {
            const current = prev[rowKey] || {};
            const updated = { ...current, [field]: value };

            if (field === 'hoursWorked') {
                const total = Number(value) || 0;
                if (total > DAILY_REGULAR_HOURS_THRESHOLD) {
                    updated.hoursWorked = DAILY_REGULAR_HOURS_THRESHOLD;
                    updated.otHours     = Math.round((total - DAILY_REGULAR_HOURS_THRESHOLD) * 100) / 100;
                } else {
                    updated.hoursWorked = total;
                }
            }

            if (field === 'leaveType' && value && value !== 'None' && value !== '—') {
                const isUnpaidSelected = value.toLowerCase().includes('unpaid');
                const currentHours     = Number(updated.hoursWorked ?? 0);
                if (!isUnpaidSelected && currentHours === 0) updated.hoursWorked = 8;
            }

            return { ...prev, [rowKey]: updated };
        });
    };

    const cancelEdit = (rowKey) => {
        setEditingRows((prev) => { const n = { ...prev }; delete n[rowKey]; return n; });
    };

    const requestEdit = (row) => setEditConfirmRow(row);

    const confirmEdit = () => {
        const row = editConfirmRow;
        setEditConfirmRow(null);
        setEditingRows((prev) => ({
            ...prev,
            [row.key]: {
                hoursWorked: row.hoursWorked,
                otHours:     row.otHours,
                leaveType:   row.leaveType === '—' ? 'None' : row.leaveType,
            },
        }));
    };

    const persistTimeTrackingRow = useCallback(async (row) => {
        const numericRegular  = Number(row.hoursWorked || 0);
        const numericOvertime = Number(row.otHours     || 0);
        const rawLeaveType    = (!row.leaveType || row.leaveType === '—' || row.leaveType === 'None') ? null : row.leaveType;
        const isUnpaidLeave   = rawLeaveType && rawLeaveType.toLowerCase().includes('unpaid');
        const safeLeaveType   = (numericRegular > 0 || numericOvertime > 0) && isUnpaidLeave ? null : rawLeaveType;

        const regularHoursToEncode = safeLeaveType ? (numericRegular + numericOvertime) : numericRegular;
        const startMinutes = 9 * 60;
        const endMinutes   = startMinutes + Math.round(regularHoursToEncode * 60);
        const timeIn       = toHHMM(startMinutes);
        const timeOut      = toHHMM(endMinutes);

        const findTimeCardQuery = `
            query FindTimeCards($userId: String!, $date: String!) {
                findTimeCards(userId: $userId, date: $date) { id }
            }
        `;
        const timeCardData = await executeGraphQL(findTimeCardQuery, { userId: row.employeeId, date: row.workDate });
        const existing     = (timeCardData?.findTimeCards || [])[0];
        const otHoursValue = (!safeLeaveType && numericOvertime > 0) ? numericOvertime : undefined;

        if (existing?.id) {
            await executeGraphQL(
                `mutation UpdateTimeCard($input: UpdateTimeCardInput!) { updateTimeCard(updateTimeCardInput: $input) { id } }`,
                { input: { id: existing.id, timeIn, timeOut, leaveType: safeLeaveType, leaveHours: safeLeaveType ? (numericRegular + numericOvertime) : null, ...(otHoursValue !== undefined && { otHours: otHoursValue }) } },
            );
            return;
        }
        await executeGraphQL(
            `mutation CreateTimeCard($input: CreateTimeCardInput!) { createTimeCard(createTimeCardInput: $input) { id } }`,
            { input: { userId: row.employeeId, date: row.workDate, timeIn, timeOut, leaveType: safeLeaveType, leaveHours: safeLeaveType ? (numericRegular + numericOvertime) : null, ...(otHoursValue !== undefined && { otHours: otHoursValue }) } },
        );
    }, [executeGraphQL]);

    const saveRow = async (row) => {
        const draft    = editingRows[row.key] || {};
        const rawHours = Number(draft.hoursWorked ?? row.hoursWorked);
        const regular  = Math.min(rawHours, DAILY_REGULAR_HOURS_THRESHOLD);
        const autoOt   = rawHours > DAILY_REGULAR_HOURS_THRESHOLD ? rawHours - DAILY_REGULAR_HOURS_THRESHOLD : 0;
        const overtime = autoOt > 0 ? autoOt : Number(draft.otHours ?? row.otHours);
        const updated  = { ...row, hoursWorked: regular, otHours: overtime, leaveType: draft.leaveType ?? row.leaveType, totalPayableHours: regular + overtime };

        const isUnpaid = (updated.leaveType || '').toLowerCase().includes('unpaid');
        if ((updated.hoursWorked > 0 || updated.otHours > 0) && isUnpaid) updated.leaveType = 'None';
        if ((updated.leaveType || '').toLowerCase().includes('unpaid')) updated.totalPayableHours = 0;

        if (Number.isNaN(updated.hoursWorked) || Number.isNaN(updated.otHours)) {
            setError('Hours must be valid numbers.');
            return;
        }

        try {
            setSavingRowKey(row.key);
            setError(null);
            await persistTimeTrackingRow(updated);
            setEmployees((prev) => prev.map((item) => (item.key === row.key ? updated : item)));
            cancelEdit(row.key);
            cacheRef.current.delete(dateRange);
        } catch (err) {
            console.error('[TimeTracking] save failed:', err);
            setError(err.message || 'Failed to save time tracking row.');
        } finally {
            setSavingRowKey(null);
        }
    };

    useEffect(() => {
        if (!token) { setError('Authentication required. Please login again.'); return; }
        const controller = new AbortController();
        fetchTimeTracking(controller.signal);
        return () => controller.abort();
    }, [fetchTimeTracking, token]);

    // ── Derived values ────────────────────────────────────────────────────────
    const stats = useMemo(() => ({
        totalHours:     totals.totalRegularHours + totals.totalOvertimeHours + totals.totalPaidLeaveHours + totals.totalUnpaidLeaveHours,
        overtimeHours:  totals.totalOvertimeHours,
        paidLeaveHrs:   totals.totalPaidLeaveHours,
        unpaidLeaveHrs: totals.totalUnpaidLeaveHours,
    }), [totals]);

    const filtered = useMemo(() => {
        const q = debouncedSearch.toLowerCase();
        return employees.filter((e) => {
            const hasHours = Number(e.hoursWorked || 0) > 0 || Number(e.otHours || 0) > 0;
            const hasLeave = e.leaveType && e.leaveType !== '—' && e.leaveType.toLowerCase() !== 'none';
            if (!hasHours && !hasLeave) return false;
            return (
                (e.employeeName.toLowerCase().includes(q) || e.employeeId.toLowerCase().includes(q)) &&
                matchesLeaveFilter(e)
            );
        });
    }, [employees, debouncedSearch, matchesLeaveFilter]);

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="tt-page">

            {/* Header */}
            <div className="tt-header">
                <div>
                    <h2 className="tt-title">Time & Work Tracking</h2>
                    <p className="tt-subtitle">Record employee hours and leave for payroll calculation</p>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="tt-cards">
                <div className="tt-stat-card">
                    <div className="tt-stat-top"><span>Total Hours</span><FiClock className="tt-stat-icon blue" /></div>
                    <div className="tt-stat-value">{stats.totalHours} hrs</div>
                </div>
                <div className="tt-stat-card">
                    <div className="tt-stat-top"><span>Overtime Hours</span><FiBriefcase className="tt-stat-icon orange" /></div>
                    <div className="tt-stat-value">{stats.overtimeHours} hrs</div>
                </div>
                <div className="tt-stat-card">
                    <div className="tt-stat-top"><span>Paid Leave Hours</span><FiCheckCircle className="tt-stat-icon green" /></div>
                    <div className="tt-stat-value">{stats.paidLeaveHrs} hrs</div>
                </div>
                <div className="tt-stat-card">
                    <div className="tt-stat-top"><span>Unpaid Leave Hours</span><FiAlertCircle className="tt-stat-icon red" /></div>
                    <div className="tt-stat-value">{stats.unpaidLeaveHrs} hrs</div>
                </div>
            </div>

            {/* Filter bar */}
            <div className="tt-filter-bar">
                <div className="tt-search">
                    <FiSearch size={14} color="#94a3b8" />
                    <input
                        placeholder="Search Employee Name"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        aria-label="Search employees"
                    />
                </div>
                <div className="tt-filter-right">
                    <div className="tt-filter-chip">
                        <FiFilter size={13} color="#64748b" />
                        <select value={leaveFilter} onChange={(e) => setLeaveFilter(e.target.value)} aria-label="Leave filter">
                            {LEAVE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <div className="tt-filter-chip">
                        <FiCalendar size={13} color="#64748b" />
                        <select value={dateRange} onChange={(e) => setDateRange(e.target.value)} aria-label="Date range">
                            {DATE_RANGE_OPTIONS.map((o) => <option key={o}>{o}</option>)}
                        </select>
                    </div>
                </div>
            </div>

            {/* Table section */}
            <div className="tt-section">

                {error && (
                    <div className="tt-error">
                        <FiAlertCircle size={16} />
                        <span>{error}</span>
                    </div>
                )}

                {isRefreshing && (
                    <div className="tt-refresh-hint">Refreshing latest backend data...</div>
                )}

                {!error && (
                    <div className="tt-table-wrap">
                        <table className="tt-table">
                            <thead>
                                <tr>
                                    <th>Employee</th>
                                    <th>Date</th>
                                    <th>Hours Worked</th>
                                    <th>OT Hours</th>
                                    <th>Leave Type</th>
                                    <th>Total Payable Hours</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading && filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="tt-empty-row">
                                            <CommonLoader message="Loading timesheets…" />
                                        </td>
                                    </tr>
                                ) : filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={7} className="tt-empty-row">
                                            {debouncedSearch
                                                ? `No entries match "${debouncedSearch}".`
                                                : 'No entries found for this period.'}
                                        </td>
                                    </tr>
                                ) : filtered.map((row) => (
                                    <tr key={row.key}>
                                        <td>
                                            <div className="tt-emp-name">{row.employeeName}</div>
                                        </td>
                                        <td>{fmtDisplayDate(row.workDate)}</td>
                                        <td>
                                            {editingRows[row.key] ? (
                                                <input type="number" min="0" step="0.5" className="tt-inline-input"
                                                    value={editingRows[row.key]?.hoursWorked ?? row.hoursWorked}
                                                    onChange={(e) => handleEditChange(row.key, 'hoursWorked', e.target.value)} />
                                            ) : <span className="tt-cell-input-like">{row.hoursWorked}</span>}
                                        </td>
                                        <td>
                                            {editingRows[row.key] ? (
                                                <input type="number" min="0" step="0.5" className="tt-inline-input"
                                                    value={editingRows[row.key]?.otHours ?? row.otHours}
                                                    onChange={(e) => handleEditChange(row.key, 'otHours', e.target.value)} />
                                            ) : <span className="tt-cell-input-like">{row.otHours}</span>}
                                        </td>
                                        <td>
                                            {editingRows[row.key] ? (
                                                <select className="tt-inline-select"
                                                    value={editingRows[row.key]?.leaveType ?? (row.leaveType === '—' ? 'None' : row.leaveType)}
                                                    onChange={(e) => handleEditChange(row.key, 'leaveType', e.target.value)}>
                                                    {LEAVE_EDIT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                                                </select>
                                            ) : (
                                                <span className="tt-cell-input-like tt-leave-cell">
                                                    {row.leaveType === '—' ? 'None' : row.leaveType}
                                                </span>
                                            )}
                                        </td>
                                        <td className="tt-total-hours">{row.totalPayableHours} hrs</td>
                                        <td className="tt-action-cell">
                                            {editingRows[row.key] ? (
                                                <div className="tt-action-buttons">
                                                    <button className="tt-row-btn save" onClick={() => saveRow(row)} disabled={savingRowKey === row.key}>
                                                        <FiSave size={12} />
                                                        {savingRowKey === row.key ? 'Saving...' : 'Save'}
                                                    </button>
                                                    <button className="tt-row-btn cancel" onClick={() => cancelEdit(row.key)} disabled={savingRowKey === row.key}>
                                                        <FiX size={12} />
                                                        Cancel
                                                    </button>
                                                </div>
                                            ) : (
                                                <button className="tt-row-btn edit" onClick={() => requestEdit(row)}>
                                                    <FiEdit2 size={11} />
                                                    Edit
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ── Edit Confirmation Popup ──────────────────────────────────── */}
            {editConfirmRow && (
                <div className="tt-overlay" onClick={() => setEditConfirmRow(null)}>
                    <div className="tt-confirm-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="tt-confirm-icon">
                            <FiEdit2 size={22} />
                        </div>
                        <h3 className="tt-confirm-title">Edit Timesheet Entry?</h3>
                        <p className="tt-confirm-desc">
                            You are about to edit the timesheet for&nbsp;
                            <strong>{editConfirmRow.employeeName}</strong>&nbsp;on&nbsp;
                            <strong>{fmtDisplayDate(editConfirmRow.workDate)}</strong>.
                        </p>
                        <div className="tt-confirm-fields">
                            <div className="tt-confirm-field-item">
                                <span className="tt-confirm-dot green" />
                                <span>Worked Hours</span>
                                <span className="tt-confirm-current">{editConfirmRow.hoursWorked} hrs</span>
                            </div>
                            <div className="tt-confirm-field-item">
                                <span className="tt-confirm-dot orange" />
                                <span>OT Hours</span>
                                <span className="tt-confirm-current">{editConfirmRow.otHours} hrs</span>
                            </div>
                            <div className="tt-confirm-field-item">
                                <span className="tt-confirm-dot blue" />
                                <span>Leave Type</span>
                                <span className="tt-confirm-current">
                                    {editConfirmRow.leaveType === '—' ? 'None' : editConfirmRow.leaveType}
                                </span>
                            </div>
                        </div>
                        <p className="tt-confirm-warning">
                            ⚠️ If payroll has already been processed for this period, editing hours will create a payroll adjustment applied in the next run.
                        </p>
                        <div className="tt-confirm-actions">
                            <button className="tt-confirm-btn cancel" onClick={() => setEditConfirmRow(null)}>
                                Cancel
                            </button>
                            <button className="tt-confirm-btn proceed" onClick={confirmEdit}>
                                Yes, Edit Entry
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}
